import { TutorialTrackId } from "../../game/GameState";
import { STORY_SCENES, StorySceneId } from "../../game/story";
import {
  TUTORIAL_TRACKS,
  TutorialStep,
  TutorialStepId,
  trackSteps,
} from "../../game/tutorial";
import { MilestoneLedger } from "./milestones";

/**
 * The demo's spine: its milestones in the order a player meets them,
 * read off the game's own definitions rather than off timestamps. The
 * telemetry page draws its funnel down this list, and every event a
 * client sends is stamped with the furthest key on it the player has
 * reached (`at`), so what happened can be grouped by the step it
 * happened during.
 *
 * The order is the story's: each scene in the order `STORY_SCENES`
 * lists them, followed by the tutorial steps whose thought that scene
 * reveals, in walk order. That is one line through the guided opening.
 * The other two tracks — the first night, and sweeping — are side
 * lines: they begin on their own condition (the sun going down, the
 * floor getting dusty) at no fixed point in the main line, so they
 * are kept apart, each with the scene that reveals its steps, and
 * they don't count toward a player's position. A player who slept on
 * day one is not "further along" than one still prying nails.
 *
 * A key is `scene:<id>` or `step:<id>` (step ids are unique across
 * tracks), and `START` stands for a playthrough nothing has happened
 * in yet.
 */

export const START = "start";

export interface SpineMilestone {
  readonly key: string;
  readonly kind: "scene" | "step";
  readonly id: StorySceneId | TutorialStepId;
  /** The tutorial track a step belongs to; scenes have none. */
  readonly track?: TutorialTrackId;
  /** The step's own label, or a readable form of the scene's id. */
  readonly label: string;
}

export interface SpineLine {
  readonly id: "main" | TutorialTrackId;
  readonly title: string;
  readonly milestones: ReadonlyArray<SpineMilestone>;
}

const MAIN_TRACK: TutorialTrackId = "opening";

const LINE_TITLES: Record<TutorialTrackId, string> = {
  opening: "The guided opening",
  firstNight: "The first night",
  dust: "Sweeping",
};

/** The key for a milestone id as recorded — the data's strings, untyped. */
export const keyOf = (kind: "scene" | "step", id: string): string =>
  `${kind}:${id}`;
export const sceneKey = (id: StorySceneId): string => keyOf("scene", id);
export const stepKey = (id: TutorialStepId): string => keyOf("step", id);

/** "palletFound" → "Pallet found". */
function humanize(id: string): string {
  const spaced = id.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function stepMilestone(
  track: TutorialTrackId,
  step: TutorialStep,
): SpineMilestone {
  return {
    key: stepKey(step.id),
    kind: "step",
    id: step.id,
    track,
    label: step.label,
  };
}

/** The track whose steps a scene reveals, or the main line when it
 * reveals none (a verdict scene, a piece she comes to see). */
function lineOfScene(id: StorySceneId): TutorialTrackId {
  for (const track of TUTORIAL_TRACKS) {
    if (track.id === MAIN_TRACK) continue;
    if (trackSteps(track.id).some((step) => step.revealedBy === id)) {
      return track.id;
    }
  }
  return MAIN_TRACK;
}

function buildLines(): ReadonlyArray<SpineLine> {
  const byLine = new Map<TutorialTrackId, SpineMilestone[]>(
    TUTORIAL_TRACKS.map((track) => [track.id, []]),
  );
  const placed = new Set<string>();
  for (const scene of STORY_SCENES) {
    const line = lineOfScene(scene.id);
    const list = byLine.get(line)!;
    list.push({
      key: sceneKey(scene.id),
      kind: "scene",
      id: scene.id,
      label: humanize(scene.id),
    });
    for (const track of TUTORIAL_TRACKS) {
      for (const step of trackSteps(track.id)) {
        if (step.revealedBy !== scene.id) continue;
        const milestone = stepMilestone(track.id, step);
        placed.add(milestone.key);
        byLine.get(track.id)!.push(milestone);
      }
    }
  }
  // A step no scene reveals goes at the end of its track.
  for (const track of TUTORIAL_TRACKS) {
    for (const step of trackSteps(track.id)) {
      const milestone = stepMilestone(track.id, step);
      if (!placed.has(milestone.key)) byLine.get(track.id)!.push(milestone);
    }
  }
  return TUTORIAL_TRACKS.map((track) => ({
    id: track.id === MAIN_TRACK ? "main" : track.id,
    title: LINE_TITLES[track.id],
    milestones: byLine.get(track.id)!,
  }));
}

/** Every line, the main one first. */
export const SPINE_LINES: ReadonlyArray<SpineLine> = buildLines();

/** The main line: the guided opening and the story around it. */
export const MAIN_LINE: SpineLine = SPINE_LINES[0];

const MAIN_INDEX = new Map(MAIN_LINE.milestones.map((m, i) => [m.key, i]));

const BY_KEY = new Map(
  SPINE_LINES.flatMap((line) => line.milestones).map((m) => [m.key, m]),
);

/** The milestone a key names, or null for START and anything unknown. */
export function milestoneByKey(key: string): SpineMilestone | null {
  return BY_KEY.get(key) ?? null;
}

/** A key's index on the main line; -1 for START and for side-line keys. */
export function mainIndex(key: string): number {
  return MAIN_INDEX.get(key) ?? -1;
}

/**
 * The further of two positions on the main line. A side-line key never
 * moves the position; START is behind everything.
 */
export function furtherOf(a: string, b: string): string {
  return mainIndex(b) > mainIndex(a) ? b : a;
}

/** Where a ledger stands: the furthest main-line key it has reached. */
export function positionOf(ledger: MilestoneLedger): string {
  let at = START;
  for (const id of ledger.scenes) at = furtherOf(at, sceneKey(id));
  const steps = trackSteps(MAIN_TRACK);
  const passed = Math.min(ledger.steps[MAIN_TRACK], steps.length);
  for (let i = 0; i < passed; i++) at = furtherOf(at, stepKey(steps[i].id));
  return at;
}
